import * as React from 'react'
import { useQuery } from '@tanstack/react-query'
import { ChevronRight } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { EmptyState } from '@/components/ui/empty-state'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { getJSON } from '@/lib/api'
import type { ProfileGroup } from '@/lib/contacts/types'
import { cn } from '@/lib/utils'
import { ContactGroupsTab } from '@/pages/ContactGroupsTab'

interface PeopleGroupPerson {
  person_id: number | null
  person_name: string
  level_name: string | null
}
interface PeopleGroup {
  id: number
  name: string
  people_count: number
  people: PeopleGroupPerson[]
}
interface PeopleGroupsResponse {
  rows: PeopleGroup[]
  total: number
  next_cursor: string | null
}

const plural = (count: number, word: string) => `${count} ${word}${count === 1 ? '' : 's'}`
const peopleLabel = (count: number) => (count === 1 ? '1 person' : `${count} people`)

/** The Groups tab: the Groups the organisation's people sit in, then the organisation's own Groups. */
export function OrganizationGroupsTab({
  organizationId,
  groups,
}: {
  organizationId: number
  groups: ProfileGroup[]
}) {
  return (
    <div className="space-y-8">
      <OrganizationPeopleGroups organizationId={organizationId} />
      <section aria-labelledby="organization-groups-heading" className="space-y-1">
        <h3 id="organization-groups-heading" className="text-body font-semibold text-foreground">
          Groups this organisation is in
        </h3>
        <ContactGroupsTab
          contactId={organizationId}
          groups={groups}
          profileQueryKey={['organization-profile', organizationId]}
        />
      </section>
    </div>
  )
}

/** The Groups the organisation's people sit in, read from the staff Engagement Groups list. */
export function OrganizationPeopleGroups({ organizationId }: { organizationId: number }) {
  const [cursors, setCursors] = React.useState<string[]>([])
  const cursor = cursors.at(-1)
  const path = `/api/v1/contacts/${organizationId}/engagement/groups${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ''}`
  const query = useQuery({
    queryKey: ['organization-people-groups', organizationId, cursor],
    queryFn: () => getJSON<PeopleGroupsResponse>(path),
    retry: false,
  })

  return (
    <section aria-labelledby="people-groups-heading" className="space-y-3">
      <div>
        <h3 id="people-groups-heading" className="text-body font-semibold text-foreground">
          Groups its people sit in
        </h3>
        <p className="text-body-sm text-muted-foreground">
          Groups with at least one person affiliated with this organisation today.
        </p>
      </div>
      {query.isPending && <EmptyState title="Loading Groups" variant="quiet" />}
      {query.isError && <EmptyState title="Groups unavailable" variant="quiet" />}
      {query.data && query.data.total === 0 && (
        <EmptyState
          title="None of its people sit in a Group"
          variant="quiet"
          className="items-start px-0 text-left"
        />
      )}
      {query.data && query.data.total > 0 && (
        <div className="overflow-hidden rounded-md border border-border bg-card">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Group</TableHead>
                <TableHead className="text-right">People</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {query.data.rows.map((group) => (
                <PeopleGroupRow key={group.id} group={group} />
              ))}
            </TableBody>
          </Table>
          <div className="flex items-center justify-between border-t border-border px-4 py-3">
            <span className="text-caption text-muted-foreground">
              {plural(query.data.total, 'group')}
            </span>
            <div className="flex gap-2">
              {cursor && (
                <Button variant="outline" size="sm" onClick={() => setCursors([])}>
                  First page
                </Button>
              )}
              {query.data.next_cursor && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCursors([...cursors, query.data.next_cursor!])}
                >
                  Next page
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
    </section>
  )
}

function PeopleGroupRow({ group }: { group: PeopleGroup }) {
  const [open, setOpen] = React.useState(false)
  const listId = `people-group-${group.id}`
  return (
    <TableRow>
      <TableCell className="align-top">
        <Button
          type="button"
          variant="ghost"
          size="inline"
          aria-expanded={open}
          aria-controls={listId}
          className="flex gap-1.5 font-medium"
          onClick={() => setOpen(!open)}
        >
          <ChevronRight
            aria-hidden="true"
            className={cn('size-4 transition-transform', open && 'rotate-90')}
          />
          {group.name}
        </Button>
        {open && (
          <ul id={listId} className="space-y-1.5 pb-1 pl-6 pt-2">
            {group.people.map((person, index) => (
              <li key={person.person_id ?? `erased-${index}`} className="flex items-center gap-2">
                {person.person_id ? (
                  <Button variant="link" size="inline" asChild className="font-normal">
                    <a href={`/contacts/persons/${person.person_id}`}>{person.person_name}</a>
                  </Button>
                ) : (
                  <span className="text-body-sm text-muted-foreground">{person.person_name}</span>
                )}
                {person.level_name && <Badge variant="neutral">{person.level_name}</Badge>}
              </li>
            ))}
          </ul>
        )}
      </TableCell>
      <TableCell className="align-top text-right tabular-nums text-muted-foreground">
        {peopleLabel(group.people_count)}
      </TableCell>
    </TableRow>
  )
}
